/**
 * original source: https://ui.shadcn.com/docs/components/textarea
 */
import type { VariantProps } from '@joyful/utils'
import { cn, tv } from '@joyful/utils'
import type { ComponentProps } from 'react'
export const textareaVariants = tv({
  base: 'flex items-center border-0 bg-tremor-background dark:bg-tremor-background-muted text-sm text-tremor-content-strong transition duration-100 placeholder:text-tremor-content-subtle focus-visible:outline-hidden disabled:opacity-50 aria-invalid:ring-red-500 dark:aria-invalid:ring-red-400 data-[autofilled=true]:bg-tremor-brand-faint',

  defaultVariants: {
    focusVariant: 'ring',
    roundingVariant: 'default',
    shouldAutosize: false,
    variant: 'default',
  },

  variants: {
    focusVariant: {
      'focus-ring': 'focus:ring-2 focus:ring-tremor-brand-subtle',
      none: 'focus:ring-0',
      ring: 'ring-inset ring-1 ring-tremor-border focus:ring-2 focus:ring-tremor-brand-subtle',
    },

    roundingVariant: {
      bottom: 'rounded-b-md',
      default: 'rounded-md',
      none: null,
    },

    shouldAutosize: {
      true: 'field-sizing-content max-h-[var(--textarea-max-height,12.5rem)] min-h-[calc(var(--textarea-rows)*1lh)]',
    },

    variant: {
      default: null,
      transparent:
        'resize-none bg-transparent shadow-none! hover:bg-transparent',
    },
  },
})

export interface TextareaProps
  extends ComponentProps<'textarea'>, VariantProps<typeof textareaVariants> {
  maxHeight?: string
  /**
   * @default false
   */
  shouldEnablePasswordManagers?: boolean

  value?: string
}

function Textarea({
  className,
  focusVariant,
  maxHeight,
  roundingVariant,
  rows,
  shouldAutosize,
  shouldEnablePasswordManagers = false,
  style,
  variant,
  ...props
}: TextareaProps) {
  return (
    <textarea
      className={cn(
        textareaVariants({
          focusVariant,
          roundingVariant,
          shouldAutosize,
          variant,
        }),
        className,
      )}
      rows={rows}
      style={{
        '--textarea-max-height': maxHeight,
        '--textarea-rows': rows,
        ...style,
      }}
      {...(shouldEnablePasswordManagers
        ? {}
        : {
            'data-1p-ignore': true,
            'data-bwignore': true,
            'data-lpignore': true,
            'data-protonpass-ignore': true,
          })}
      {...props}
    />
  )
}

export { Textarea }
